import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  DialogService,
  DialogRef,
  DIALOG_DATA,
} from 'src/app/components/dialog';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SnackbarService } from 'src/app/components/snackbar/snackbar.service';

import { CompanyService } from 'src/app/services/company.service';
import { IEditionContent } from 'src/app/components/edition/entities/edition.interfaces';
import {
  IInterval,
  ISituation,
  ISituationAPIResponse,
} from 'src/app/components/form-components/entities/form-components.interfaces';
import { FormSituationsService } from 'src/app/components/form-components/situations/situations.service';
import {
  IExtendedSickNote,
  // TODO Enable Rais Reason
  // RaisReason,
  Situation,
} from 'src/app/shared/model/sick_note';
import { RefusalReasonService } from 'src/app/services/refusal-reason.service';
import { Reason } from 'src/app/shared/model/refusal-reasons.model';
import { EagerMaxLengthErrorStateMatcher } from 'src/app/components/form-field/error-state-matcher';
import {
  EmployeeMessageLimitService,
  employeeMessageLimitValidator,
} from 'src/app/services/employee-message-limit.service';

export interface ValidateDialogResult {
  changes: IEditionContent;
  message: string;
  action: boolean;
  situationsError?: boolean;
}

export interface ValidateDialogData {
  interval?: IInterval;
  message?: string;
  typeId?: number;
  situations?: ISituation[];

  /**
   * @deprecated
   * Only used in sick-notes-old-form
   * */
  sickNote?: IExtendedSickNote;
  situations$?: Observable<Situation[]>;
}

@Component({
  selector: 'app-validate-dialog',
  templateUrl: './validate-dialog.component.html',
})
export class ValidateDialogComponent implements OnInit {
  form: UntypedFormGroup;
  situationsDataSource: ISituationAPIResponse[] | null = null;
  multipleAllowed$: Observable<boolean>;
  changes = { values: {} } as IEditionContent;
  hasMoreThanOneMessage = false;
  readonly messageErrorMatcher = new EagerMaxLengthErrorStateMatcher();
  readonly messageLimit = this.employeeMessageLimit.limit;

  constructor(
    public dialogRef: DialogRef<ValidateDialogComponent, ValidateDialogResult>,
    private formBuilder: UntypedFormBuilder,
    private companyService: CompanyService,
    private formSituationsService: FormSituationsService,
    @Inject(DIALOG_DATA) public data: ValidateDialogData,
    private refusalReasonService: RefusalReasonService,
    private snackbarService: SnackbarService,
    private employeeMessageLimit: EmployeeMessageLimitService
  ) {
    this.form = this.formBuilder.group({
      situations: [this.data.situations || []],
      reason: [null],
      message: [
        this.data.message || '',
        employeeMessageLimitValidator(this.messageLimit),
      ],
    });

    this.multipleAllowed$ = this.companyService.settings$.pipe(
      map((s) => !!s?.multiple_situations)
    );
    this.employeeMessageLimit.track(this.message, 'approval_dialog');
  }

  get situations() {
    return this.form.get('situations');
  }

  get message() {
    return this.form.get('message')!;
  }

  get approveDisabled(): boolean {
    return (
      !this.situationsDataSource ||
      (!!this.situationsDataSource.length && !this.situations?.value?.length) ||
      this.message.invalid
    );
  }

  static open(
    dialog: DialogService,
    data?: ValidateDialogData
  ): DialogRef<ValidateDialogComponent, ValidateDialogResult> {
    return dialog.open(ValidateDialogComponent, {
      panelClass: 'simple-dialog',
      width: '700px',
      closeOnNavigation: false,
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90%',
      data,
    });
  }

  ngOnInit(): void {
    this.formSituationsService.get(this.data.typeId).subscribe({
      next: (situations: ISituationAPIResponse[]) => {
        this.situationsDataSource = situations || [];
      },
      error: () =>
        this.dialogRef.close({
          action: false,
          changes: this.changes,
          message: this.form.get('message')?.value,
          situationsError: true,
        }),
    });

    if (!!this.situations)
      this.situations.valueChanges.subscribe(
        (situations) => (this.changes.values.situations = situations)
      );

    this.setApprovalReasons();
    this.setReasonObserver();
  }

  setReasonObserver() {
    this.form.get('reason')!.valueChanges.subscribe((reason: Reason) => {
      this.form.patchValue({
        message: reason?.message ?? '',
      });
    });
  }

  setApprovalReasons() {
    this.refusalReasonService.getList({ type: 'approval' }).subscribe({
      next: (messages) => {
        if (messages.length > 1) this.hasMoreThanOneMessage = true;
        else if (messages.length === 1)
          this.form.patchValue({
            message: messages?.[0].message ?? '',
          });
      },
      error: () => {
        this.handleError(
          'Falha ao carregar mensagens de aprovação, favor tente novamente.'
        );
      },
    });
  }

  onNoClicked(): void {
    this.dialogRef.close();
  }

  onOkClick(): void {
    this.dialogRef.close({
      action: true,
      changes: this.changes,
      message: this.form.get('message')?.value,
    });
  }

  private handleError(message: string) {
    this.snackbarService.status({
      action: 'Fechar',
      message,
      state: 'error',
    });
  }
}
